export function genAIConsent(userStatus: any): boolean {
  return (
    !dnmAIConsent(userStatus) &&
    isUserDnmAIConsent(userStatus) &&
    isGenAIConsentDate() &&
    isUserDnmEntitlement(userStatus)
  );
}

/**
 * Validates that all prerequisites for GenAI access (e.g AI Assistant Button) checks are in place for further processing.
 * Checks for:
 * - Valid approved entitlements config
 * - Valid approved roles config
 * - User has roles array
 * - User has bundles.entitlements array
 */
export function hasValidGenAIPrerequisites(
  approvedEnt: string[],
  userStatus: any
): boolean {
  // Guard clause for no approved entitlements
  if (approvedEnt.length === 0) {
    return false;
  }

  const approvedRoleConfig = window.env.APPROVED_AI_ROLE_ID;

  // Guard clause for no approved roles
  if (
    !approvedRoleConfig ||
    (approvedRoleConfig.all_access?.length === 0 &&
      approvedRoleConfig.dnm_consent?.length === 0)
  ) {
    return false;
  }

  // Guard clauses for missing user data
  if (!userStatus?.["user-roles"]) {
    return false;
  }
  if (!userStatus?.bundles?.entitlements) {
    return false;
  }

  return true;
}

/**
 * Checks if user has any approved AI entitlements
 * Compares user's bundle entitlements against the approved entitlements list
 * Matches on entitlement_id, product_id, or content_bundle_name
 */
export function hasApprovedEntitlement(
  approvedEnt: string[],
  userStatus: any
): boolean {
  return approvedEnt.some((ent: string) =>
    userStatus?.bundles?.entitlements?.some(
      (bundEnt: {
        content_bundle_name: string;
        entitlement_id: string;
        product_id: string;
      }) =>
        ent &&
        (ent == bundEnt.entitlement_id ||
          ent == bundEnt.product_id ||
          ent == bundEnt.content_bundle_name)
    )
  );
}

/**
 * Checks if user has GenAI role-based access
 * Evaluates both all_access roles (immediate access) and dnm_consent roles (requires consent)
 */
export function hasGenAIRoleAccess(userStatus: any): boolean {
  const approvedRoleConfig = window.env.APPROVED_AI_ROLE_ID;

  // Extract user's role IDs
  const userRoleIds = userStatus["user-roles"].map(
    (userRole: { roleId: string }) => userRole.roleId
  );

  // Check all_access roles - grants immediate access
  const hasAllAccessRole = approvedRoleConfig.all_access?.some(
    (roleId: string) => userRoleIds.includes(roleId)
  );

  if (hasAllAccessRole) {
    return true;
  }

  // Check dnm_consent roles - requires consent flag
  const hasDnmConsentRole = approvedRoleConfig.dnm_consent?.some(
    (roleId: string) => userRoleIds.includes(roleId)
  );

  if (hasDnmConsentRole && userStatus?.dnmAIConsent) {
    return true;
  }

  return false;
}

/**
 * Parses the approved AI entitlements from environment config
 * @returns Array of approved entitlement strings
 */
export function getAiApprovedEntitlements(): string[] {
  return (
    window.env?.APPROVED_AI_ENTITLEMENTS?.split("|")
      .map((item: string) => item.trim())
      .filter(Boolean) ?? []
  );
}

/**
 * Checks if user has AI Assistant entitlement (grants immediate access)
 */
function hasAIAssistantEntitlement(userStatus: any): boolean {
  const aiAssistantEntitlement =
    window.env.APPROVED_AI_ASSISTANT_ENTITLEMENT ?? "";
  return (
    aiAssistantEntitlement !== "" &&
    hasApprovedEntitlement([aiAssistantEntitlement], userStatus)
  );
}

/**
 * Main function to check if user has GenAI access (e.g., AI Assistant Button)
 * Orchestrates all prerequisite, entitlement, and role-based access checks
 * @param userStatus - User status object containing roles, bundles, and consent data
 * @returns boolean indicating if user has GenAI access
 */
export function hasGenAIAccess(userStatus: any): boolean {
  if (!userStatus) {
    return false;
  }

  // AI Assistant entitlement grants immediate access
  if (hasAIAssistantEntitlement(userStatus)) {
    return true;
  }

  const approvedEnt = getAiApprovedEntitlements();

  // Validate prerequisites, entitlements, and role-based access
  return (
    hasValidGenAIPrerequisites(approvedEnt, userStatus) &&
    hasApprovedEntitlement(approvedEnt, userStatus) &&
    hasGenAIRoleAccess(userStatus)
  );
}

function isGenAIConsentDate() {
  const from = new Date(window.env.DNM_AI_CONSENT_DATE_START);
  const to = new Date(window.env.DNM_AI_CONSENT_DATE_END);
  const todayUTC = new Date().toISOString();
  return new Date(todayUTC) >= from && new Date(todayUTC) <= to;
}

function dnmAIConsent(userStatus: any): boolean {
  return userStatus?.dnmAIConsent ?? false;
}

function isUserDnmAIConsent(userStatus: any): boolean {
  return (
    userStatus?.["user-roles"]?.some(
      (role: any) => role?.roleKey == window.env.APPROVED_DNM_AI_ROLE
    ) ?? false
  );
}

function isUserDnmEntitlement(userStatus: any): boolean {
  const approvedEnt = getAiApprovedEntitlements();
  return (
    userStatus?.bundles?.entitlements.some(
      (entitlement: any) => entitlement?.entitlement_id == approvedEnt[1]
    ) ?? false
  );
}

export function canShowGenAIModal(userStatus: any): boolean {
  // Don't show modal if user has already given consent
  if (userStatus?.genAIConsent) {
    return false;
  }

  // Check if user has any approved entitlements
  if (!hasApprovedEntitlement(getAiApprovedEntitlements(), userStatus)) {
    return false;
  }

  // Only show modal for users with gen ai role
  const approvedRoleIds = window.env.APPROVED_USER_AI_ROLE ?? [];
  const userRoleIds = userStatus["user-roles"]?.map(
    (userRole: { roleId: string }) => userRole.roleId
  );

  return (
    approvedRoleIds?.some((roleId: string) => userRoleIds?.includes(roleId)) ??
    false
  );
}
